import { getCharacter } from "../config/characters.js";
import { achievementsFor } from "../config/achievements.js";

/**
 * ShareCard — turns a milestone (a feat unlocked, or a completed run) into a
 * polished 1200×630 image the player can post to Facebook / anywhere.
 *
 * Facebook reality: a static web page can't push a custom image straight into a
 * feed. So we do the honest, high-quality thing that works today:
 *   • generate the card image on a canvas,
 *   • offer the OS share sheet (Web Share API, carries the image file) where
 *     supported — Facebook shows up there,
 *   • plus Download, "Share to Facebook" (opens the sharer for our URL, whose
 *     Open Graph tags drive a clean link preview), and Copy link.
 * A dynamic per-share OG image needs a backend and can come later.
 */
const SHARE_URL = (() => {
  try { return location.origin + location.pathname.replace(/\/+$/, "/"); } catch (_) { return "https://relikara.game"; }
})();

export class ShareCard {
  constructor(game) {
    this.game = game;
    this._build();
    // one delegated handler covers feat share buttons wherever they render
    document.addEventListener("click", (e) => {
      const b = e.target.closest && e.target.closest("[data-share-feat]");
      if (b) { e.preventDefault(); e.stopPropagation(); this.shareFeat(b.dataset.shareFeat, b.dataset.shareHero); }
    });
  }

  _build() {
    const el = document.createElement("div");
    el.id = "shareModal";
    el.className = "share-overlay hidden";
    el.innerHTML =
      `<div class="share-backdrop" data-sclose></div>
       <div class="share-panel">
         <div class="share-head"><span class="share-title">Share</span><button class="share-x" data-sclose>✕</button></div>
         <div class="share-preview"><img class="share-img" alt="Achievement card"></div>
         <div class="share-actions">
           <button class="share-btn primary" data-act="native">Share…</button>
           <button class="share-btn" data-act="fb">Share to Facebook</button>
           <button class="share-btn" data-act="download">Download image</button>
           <button class="share-btn" data-act="copy">Copy link</button>
         </div>
         <div class="share-note"></div>
       </div>`;
    document.body.appendChild(el);
    this.el = el;
    this.img = el.querySelector(".share-img");
    this.note = el.querySelector(".share-note");
    el.querySelectorAll("[data-sclose]").forEach((n) => n.onclick = () => this.close());
    el.querySelectorAll("[data-act]").forEach((n) => n.onclick = () => this._doAction(n.dataset.act));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !this.el.classList.contains("hidden")) this.close(); });
    // hide the native-share button up front if the platform can't do file shares
    this._canNative = !!(navigator.canShare && (() => { try { return navigator.canShare({ files: [new File([""], "x.png", { type: "image/png" })] }); } catch (_) { return false; } })());
    if (!this._canNative) el.querySelector('[data-act="native"]').style.display = "none";
  }

  close() { this.el.classList.add("hidden"); }

  // ---------- entry points ----------
  shareFeat(id, heroId) {
    const list = achievementsFor(heroId || (this.game.character && this.game.character.id));
    const a = list.find((x) => x.id === id);
    if (!a) return;
    const ch = getCharacter(heroId || (this.game.character && this.game.character.id));
    this._make({
      accent: ch.color || "#c77dff",
      kicker: a.hero ? "HERO FEAT UNLOCKED" : "FEAT UNLOCKED",
      title: a.title,
      body: a.desc,
      hero: ch,
      caption: `I unlocked "${a.title}" in Relikara.`,
    });
  }

  shareRun({ depth, kills, memories = 0, fell = true }) {
    const ch = this.game.character || getCharacter();
    this._make({
      accent: ch.color || "#c77dff",
      kicker: fell ? "A RUN ENDS" : "THE VAULT CLEARED",
      title: `Depth ${depth}`,
      body: `${kills} foes slain${memories ? ` · ${memories} memories recovered` : ""}`,
      hero: ch,
      caption: `${ch.name} reached depth ${depth} in Relikara — ${kills} foes slain.`,
    });
  }

  // ---------- card rendering ----------
  async _make(opts) {
    this.note.textContent = "";
    this._caption = opts.caption || "Relikara";
    const url = await this._renderCard(opts);
    this._dataURL = url;
    this.img.src = url;
    this.el.classList.remove("hidden");
  }

  _heroPortrait(hero) {
    const icons = this.game.icons;
    if (!icons) return null;
    const wpn = (this.game.player && this.game.player.weapon && this.game.player.weapon.model) || null;
    return icons.renderCharacter(hero.model, wpn);
  }

  _loadImg(src) { return new Promise((res) => { if (!src) return res(null); const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; }); }

  async _renderCard(opts) {
    const W = 1200, H = 630;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d");
    const accent = opts.accent || "#c77dff";

    // background
    const bg = g.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#160d2a"); bg.addColorStop(1, "#080610");
    g.fillStyle = bg; g.fillRect(0, 0, W, H);
    // accent glow top-left
    const gl = g.createRadialGradient(220, 120, 20, 220, 120, 520);
    gl.addColorStop(0, this._rgba(accent, 0.22)); gl.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = gl; g.fillRect(0, 0, W, H);
    // frame
    g.strokeStyle = this._rgba(accent, 0.7); g.lineWidth = 3;
    this._roundRect(g, 18, 18, W - 36, H - 36, 20); g.stroke();

    // hero portrait (left)
    const port = await this._loadImg(this._heroPortrait(opts.hero));
    if (port) {
      const ph = 460, pw = ph * (port.width / port.height || 1);
      g.save(); g.shadowColor = this._rgba(accent, 0.5); g.shadowBlur = 40;
      g.drawImage(port, 80, (H - ph) / 2 + 10, pw, ph);
      g.restore();
    }

    // text column (right)
    const x = 560;
    g.textBaseline = "alphabetic";
    g.fillStyle = "#c9a24b"; g.font = "700 34px Georgia, serif";
    g.fillText("R E L I K A R A", x, 120);

    g.fillStyle = accent; g.font = "700 26px Georgia, serif";
    g.fillText(opts.kicker, x, 210);

    g.fillStyle = "#f4eeff"; g.font = "800 76px Georgia, serif";
    this._fitText(g, opts.title, x, 296, W - x - 70, 76);

    g.fillStyle = "#c8bde4"; g.font = "400 30px Georgia, serif";
    this._wrap(g, opts.body || "", x, 356, W - x - 70, 40);

    // hero name
    g.fillStyle = this._rgba(accent, 0.95); g.font = "600 30px Georgia, serif";
    g.fillText(`— ${opts.hero.name}${opts.hero.title ? `, ${opts.hero.title}` : ""}`, x, H - 130);

    // footer
    g.fillStyle = "#8f86ad"; g.font = "500 24px Georgia, serif";
    g.fillText("An isometric dungeon-crawler · play now", x, H - 70);
    g.fillStyle = "#c9a24b"; g.font = "600 24px Georgia, serif";
    g.fillText(SHARE_URL.replace(/^https?:\/\//, ""), x, H - 40);

    return c.toDataURL("image/png");
  }

  // ---------- actions ----------
  async _doAction(act) {
    if (act === "fb") {
      window.open("https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(SHARE_URL), "_blank", "width=640,height=640,noopener");
      this.note.textContent = "Facebook opened — attach the downloaded image to your post for the full card.";
    } else if (act === "download") {
      const a = document.createElement("a"); a.href = this._dataURL; a.download = "relikara-card.png"; a.click();
      this.note.textContent = "Saved. Post it anywhere — or attach it to your Facebook share.";
    } else if (act === "copy") {
      try { await navigator.clipboard.writeText(SHARE_URL); this.note.textContent = "Link copied."; }
      catch (_) { this.note.textContent = SHARE_URL; }
    } else if (act === "native") {
      try {
        const blob = await (await fetch(this._dataURL)).blob();
        const file = new File([blob], "relikara-card.png", { type: "image/png" });
        await navigator.share({ files: [file], title: "Relikara", text: this._caption, url: SHARE_URL });
      } catch (_) { /* user cancelled or unsupported */ }
    }
  }

  // ---------- canvas helpers ----------
  _rgba(hex, a) {
    const h = hex.replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  }
  _roundRect(g, x, y, w, h, r) {
    g.beginPath();
    g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
  }
  _fitText(g, text, x, y, maxW, size) {
    let s = size; g.font = `800 ${s}px Georgia, serif`;
    while (g.measureText(text).width > maxW && s > 34) { s -= 4; g.font = `800 ${s}px Georgia, serif`; }
    g.fillText(text, x, y);
  }
  _wrap(g, text, x, y, maxW, lh) {
    const words = String(text).split(" "); let line = "", yy = y;
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (g.measureText(test).width > maxW && line) { g.fillText(line, x, yy); line = w; yy += lh; }
      else line = test;
    }
    if (line) g.fillText(line, x, yy);
  }
}
